import { Badge } from "@frontend/components/ui/badge"
import { Button } from "@frontend/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@frontend/components/ui/popover"
import { Textarea } from "@frontend/components/ui/textarea"
import { useApiClient } from "@frontend/hooks/use-api"
import { useMutation } from "@tanstack/react-query"
import { Check, Edit3, MessageSquare, Play, Save } from "lucide-react"
import { useState } from "react"

type SubmissionStatus = string

interface ReviewActionBarProps {
  submissionId: string
  status: SubmissionStatus
  reviewComment: string | null
  editEnabled: boolean
  onEditEnabledChange: (enabled: boolean) => void
  onStatusChange: (newStatus: SubmissionStatus) => void
  onSaveAndAccept?: () => Promise<void>
  disabled?: boolean
}

const STATUS_LABELS: Record<string, string> = {
  in_progress: "In Progress",
  submitted: "Submitted",
  under_review: "Under Review",
  accepted: "Accepted",
  accepted_with_edits: "Accepted (Edits)",
  revision_needed: "Revision Needed"
}

const TERMINAL_STATUSES = ["accepted", "accepted_with_edits", "revision_needed"]

export function ReviewActionBar({
  submissionId,
  status,
  reviewComment,
  editEnabled,
  onEditEnabledChange,
  onStatusChange,
  onSaveAndAccept,
  disabled = false
}: ReviewActionBarProps) {
  const api = useApiClient()
  const [revisionComment, setRevisionComment] = useState("")
  const [revisionPopoverOpen, setRevisionPopoverOpen] = useState(false)

  const reviewMutation = useMutation({
    mutationFn: async (params: { action: string; review_comment?: string }) => {
      const result = (await api(`/submissions/${submissionId}/review`, {
        method: "POST",
        body: JSON.stringify(params)
      })) as { status: string }
      return result
    },
    onSuccess: (result) => {
      onStatusChange(result.status)
    }
  })

  const isTerminal = TERMINAL_STATUSES.includes(status)

  // Terminal states: show status badge + review comment
  if (isTerminal) {
    return (
      <div className="flex items-center gap-2">
        <Badge variant={status === "revision_needed" ? "destructive" : "default"} className="text-[9px] h-4 px-1">
          {STATUS_LABELS[status] ?? status}
        </Badge>
        {reviewComment ? (
          <span className="text-[10px] text-muted-foreground truncate max-w-[200px]" title={reviewComment}>
            {reviewComment}
          </span>
        ) : null}
      </div>
    )
  }

  // "submitted" state: show "Start Review" button
  if (status === "submitted") {
    return (
      <Button
        size="sm"
        className="h-6 px-2 text-[11px]"
        onClick={() => reviewMutation.mutate({ action: "under_review" })}
        disabled={disabled || reviewMutation.isPending}
      >
        <Play className="size-3 mr-1" />
        {reviewMutation.isPending ? "Starting..." : "Start Review"}
      </Button>
    )
  }

  // "under_review" state: Accept / Accept with Edits / Request Revision
  if (status === "under_review") {
    // If editing is enabled, show "Save & Accept" button
    if (editEnabled) {
      return (
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 text-[11px]"
            onClick={() => onEditEnabledChange(false)}
            disabled={disabled || reviewMutation.isPending}
          >
            Cancel Edit
          </Button>
          <Button
            size="sm"
            className="h-6 px-2 text-[11px]"
            onClick={async () => {
              if (onSaveAndAccept) {
                await onSaveAndAccept()
              }
              reviewMutation.mutate({ action: "accepted_with_edits" })
            }}
            disabled={disabled || reviewMutation.isPending}
          >
            <Save className="size-3 mr-1" />
            {reviewMutation.isPending ? "Saving..." : "Save & Accept"}
          </Button>
        </div>
      )
    }

    return (
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          className="h-6 px-2 text-[11px]"
          onClick={() => reviewMutation.mutate({ action: "accepted" })}
          disabled={disabled || reviewMutation.isPending}
        >
          <Check className="size-3 mr-1" />
          Accept
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-6 px-2 text-[11px]"
          onClick={() => onEditEnabledChange(true)}
          disabled={disabled || reviewMutation.isPending}
        >
          <Edit3 className="size-3 mr-1" />
          Accept with Edits
        </Button>
        <Popover open={revisionPopoverOpen} onOpenChange={setRevisionPopoverOpen}>
          <PopoverTrigger
            render={
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[11px]"
                disabled={disabled || reviewMutation.isPending}
              />
            }
          >
            <MessageSquare className="size-3 mr-1" />
            Revision
          </PopoverTrigger>
          <PopoverContent className="w-72 p-3" align="end">
            <div className="space-y-2">
              <p className="text-[11px] font-medium">Request Revision</p>
              <Textarea
                placeholder="Describe what needs to be revised..."
                className="text-[11px] min-h-[60px]"
                value={revisionComment}
                onChange={(e) => setRevisionComment(e.target.value)}
              />
              <Button
                size="sm"
                className="h-6 px-2 text-[11px] w-full"
                onClick={() => {
                  reviewMutation.mutate({
                    action: "revision_needed",
                    review_comment: revisionComment || undefined
                  })
                  setRevisionPopoverOpen(false)
                  setRevisionComment("")
                }}
                disabled={reviewMutation.isPending}
              >
                {reviewMutation.isPending ? "Sending..." : "Request Revision"}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        {reviewMutation.isError ? (
          <span className="text-[10px] text-destructive truncate max-w-[180px]">{reviewMutation.error.message}</span>
        ) : null}
      </div>
    )
  }

  // Fallback for other states
  return (
    <Badge variant="outline" className="text-[9px] h-4 px-1">
      {STATUS_LABELS[status] ?? status}
    </Badge>
  )
}
